import React, { useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import WarrantyDataItemContainer from './WarrantyDataItemContainer';
import WarrantyDataItemSummary from './WarrantyDataItemSummary';
import { Grid } from '@mui/material';
import { Dialog, Button } from '@totalsoft/rocket-ui';
import { RightsContext } from 'providers/RightsContext';
import { withParams } from 'providers/ParamsConsumer';
import { Prompt } from 'react-router-dom';
import CardNoResultsWrapper from 'components/layout/CardNoResultsWrapper';
import { BoxActionsWrapper, DetatchedAccordion, GridActionsWrapper } from 'components/common/CommonStyles';

const WarrantyDataContent = React.memo(
  ({
    warranties,
    onSaveWarranties,
    onDeleteAllWarranties,
    onDeleteWarranty,
    onAddNewWarranty,
    onPropertyChange,
    t,
    saving,
    params,
    folderOffer
  }) => {
    const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);

    const { isImage, documentWarrantiesWrite, deleteDocumentWarranties, addDocumentWarranties, readOnlyFull } = useContext(RightsContext);

    const handleDeleteAllWarranties = useCallback(() => setShowDeleteAllModal(true), []);

    const handleDeleteAllWarrantiesPositive = useCallback(() => {
      onDeleteAllWarranties();
      setShowDeleteAllModal(false);
    }, [onDeleteAllWarranties]);

    const handleDeleteAllWarrantiesClose = useCallback(() => setShowDeleteAllModal(false), []);

    return (
      <>
        <Prompt when={warranties.some(x => x.dirtyInfo?.isDirty)} message={t('LeavingWithoutSaving')} />
        <CardNoResultsWrapper content={warranties}>
          {warranties.map(({ document, validationInfo }, idx) => {
            return (
              <div key={idx}>
                <DetatchedAccordion
                  defaultExpanded={false}
                  title={
                    <WarrantyDataItemSummary
                      idx={idx}
                      warranty={document}
                      onDeleteWarranty={onDeleteWarranty}
                      warrantyItemVisible={params.fSWarrantyDetailsVisibility}
                      deleteDocumentWarranties={deleteDocumentWarranties}
                    />
                  }
                  content={
                    <WarrantyDataItemContainer
                      warranty={document}
                      onPropertyChange={onPropertyChange}
                      saving={saving}
                      validationInfo={validationInfo}
                      idx={idx}
                      warrantyItemVisible={params.fSWarrantyDetailsVisibility}
                      editRight={!!documentWarrantiesWrite && !readOnlyFull}
                      folderOffer={folderOffer}
                    />
                  }
                />
              </div>
            );
          })}
        </CardNoResultsWrapper>
        {!isImage && (
          <>
            <GridActionsWrapper container mt={2}>
              {deleteDocumentWarranties && (
                <Grid item>
                  <Button
                    id={'warrantyDataCard_deleteAllBtn'}
                    variant='outlined'
                    disabled={!warranties || warranties.length === 0 || !documentWarrantiesWrite || readOnlyFull}
                    onClick={handleDeleteAllWarranties}
                  >
                    {t('Document.Warranty.DeleteWarranty.DeleteAll')}
                  </Button>
                </Grid>
              )}
              {addDocumentWarranties && (
                <Grid item>
                  <Button id={'warrantyDataCard_addBtn'} onClick={onAddNewWarranty} disabled={!documentWarrantiesWrite || readOnlyFull}>
                    {t('Add')}
                  </Button>
                </Grid>
              )}
              <Grid item>
                <Button
                  id={'warrantyDataCard_saveBtn'}
                  onClick={onSaveWarranties}
                  disabled={
                    !warranties.some(x => x.dirtyInfo.isDirty) ||
                    warranties.some(x => !x.validationInfo.isValid) ||
                    !documentWarrantiesWrite ||
                    readOnlyFull
                  }
                >
                  {t('Save')}
                </Button>
              </Grid>
            </GridActionsWrapper>
          </>
        )}
        {showDeleteAllModal && (
          <Dialog
            open={showDeleteAllModal}
            onClose={handleDeleteAllWarrantiesClose}
            aria-labelledby='warranties-title'
            aria-describedby='warranties-description'
            title={t('Document.Warranty.DeleteWarranty.Title')}
            content={t('Document.Warranty.DeleteWarranty.DeleteAllQuestion')}
            actions={
              <BoxActionsWrapper forDialog>
                <Button id={'warrantyDataCard_deleteAllAcceptBtn'} autoFocus onClick={handleDeleteAllWarrantiesPositive}>
                  {t('Document.Warranty.DeleteWarranty.AcceptButton')}
                </Button>
                <Button id={'warrantyDataCard_deleteAllRejectBtn'} onClick={handleDeleteAllWarrantiesClose}>
                  {t('Document.Warranty.DeleteWarranty.RejectButton')}
                </Button>
              </BoxActionsWrapper>
            }
          />
        )}
      </>
    );
  }
);

WarrantyDataContent.displayName = 'WarrantyDataContent';
WarrantyDataContent.propTypes = {
  warranties: PropTypes.array.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  onAddNewWarranty: PropTypes.func.isRequired,
  onSaveWarranties: PropTypes.func.isRequired,
  onDeleteWarranty: PropTypes.func.isRequired,
  onDeleteAllWarranties: PropTypes.func.isRequired,
  params: PropTypes.object.isRequired,
  folderOffer: PropTypes.object
};

export default compose(withTranslation())(withParams(WarrantyDataContent));
